import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';

import {format, isValid, parse} from 'date-fns';

import {Icon, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Input} from '@pb/kit-component-input-v2';

import {checkDateAndGenerateError} from './utils/checkDateAndGenerateError';
import {clampDateToRange} from './utils/clampDateToRange';
import {formatValidDateToString} from './utils/formatValidDateToString';
import {getDateRange} from './utils/getDateRange';
import {expandYearOnlyInput} from './utils/expandYearOnlyInput';
import {hasTimeFormat} from './utils/hasTimeFormat';
import {identifyValueType, isDateValid} from './utils/isDateValid';
import {parseCompactDate} from './utils/parseCompactDate';
import {separatorRegex} from './utils/normalizeSeparators';

import type {OmittedInputProps} from './calendar.component';
import type {ErrorType} from './constants';
import type {Day} from 'date-fns';


interface CalendarInputProps extends OmittedInputProps {
    clearableValue?: Date,
    dateFormat: string,
    isClearable?: boolean,
    maxDate: Date,
    minDate: Date,
    onClear: () => void,
    /**
     * Callback to manually update the selected date (bypasses react-datepicker's parsing)
     */
    onDateChange?: (date: Date | null) => void,
    showWeekPicker?: boolean,
    weekStartsOn: Day,
    onValidationUpdate?: (errorType?: ErrorType, errorMessage?: string) => void,
}

export const CalendarInput = forwardRef<HTMLInputElement, CalendarInputProps>(
    (props, ref) => {
        const {
            clearableValue,
            dateFormat,
            disabled,
            readOnly,
            isClearable,
            maxDate,
            minDate,
            onBlur,
            onChange,
            onClear,
            onDateChange,
            onValidationUpdate,
            showWeekPicker = false,
            value: valueProp = '',
            weekStartsOn,
            ...inputProps
        } = props;

        const [rawValue, setRawValue] = useState<string>(valueProp);
        const inputRef = useRef<HTMLInputElement | null>(null);
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

        const formattedClearableValue = clearableValue && formatValidDateToString(clearableValue, dateFormat);

        const formatInputValue = (value: string) => {
            if (!value) {
                return '';
            }

            const compactFormattedValue = parseCompactDate(value, dateFormat);
            if (compactFormattedValue && !showWeekPicker) {
                return compactFormattedValue;
            }

            const preparedValue = {value, dateFormat, minDate, maxDate};
            const inputType = identifyValueType(preparedValue);

            if (inputType === 'date' && !showWeekPicker) {
                return value;
            }
            if (inputType === 'dateRange' && showWeekPicker) {
                return value;
            }
            if (isDateValid(preparedValue)) {
                return showWeekPicker ? getDateRange(value, weekStartsOn, dateFormat) : value;
            }

            return value;
        };

        const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
            onValidationUpdate?.();
            inputProps.onFocus?.(event);
        };

        const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            const normalizedYearValue = expandYearOnlyInput(event.target.value, dateFormat);
            const value = formatInputValue(normalizedYearValue ?? event.target.value);
            setRawValue(value);

            // Parse the date with time preserved and update the calendar
            // only if the date is valid to prevent getting error from parsing, datepicker will handle invalid dates
            if (value && onDateChange) {
                const parsedDate = parse(value, dateFormat, new Date());

                const hasTimeComponent = hasTimeFormat(dateFormat);
                const isRoundTripValid = isValid(parsedDate) && (
                    !hasTimeComponent ? format(parsedDate, dateFormat) === value : true
                );

                if (isRoundTripValid) {
                    // Clamp the date to min-max range if user typed a date outside the range
                    const clampedDate = clampDateToRange(parsedDate, minDate, maxDate);

                    // If date was clamped, update the input value to show the clamped date
                    if (clampedDate.getTime() !== parsedDate.getTime()) {
                        const clampedValue = formatValidDateToString(clampedDate, dateFormat);
                        setRawValue(clampedValue);
                    }

                    onDateChange(clampedDate);
                }
            }

            const hasSeparator = separatorRegex.test(value);
            if (onValidationUpdate) {
                if (!value) {
                    // Clear error when input is empty
                    onValidationUpdate(undefined, undefined);
                } else if (hasSeparator) {
                    // Validate when value has separator
                    const {infoMessage, errorType} = checkDateAndGenerateError({
                        value,
                        minDate,
                        maxDate,
                        dateFormat,
                    });
                    onValidationUpdate(errorType, infoMessage);
                }
            }
            onBlur?.(event);
        };

        const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
            const input = event.target as HTMLInputElement;
            const formattedValue = formatInputValue(input.value);

            if (formattedValue !== input.value) {
                input.value = formattedValue;
                setRawValue(formattedValue);
            }
        };

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setRawValue(event.target.value);
            onChange?.(event);
        };


        const showClearButton = isClearable && rawValue &&
            (!clearableValue || formattedClearableValue !== formatValidDateToString(rawValue, dateFormat));

        useEffect(() => {
            if (valueProp !== rawValue) {
                setRawValue(formatInputValue(valueProp ?? formattedClearableValue));
            }
        }, [valueProp]); // eslint-disable-line react-hooks/exhaustive-deps

        return (
            <Input
                {...inputProps}
                ref={inputRef}
                value={rawValue}
                disabled={disabled}
                readOnly={readOnly}
                onFocus={handleOnFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                onInput={handleInput}
                endAdornment={(
                    <>
                        {showClearButton ? (
                            <span className="react-datepicker__basic-icon-close">
                                <BasicIconButton
                                    disabled={disabled}
                                    aria-readonly={readOnly}
                                    aria-label="Clear Date"
                                    onClick={onClear}
                                    iconProps={{
                                        type: Icons.CLOSE,
                                        size: 'Size_19',
                                    }}
                                />
                            </span>
                        ) : null}
                        <Icon
                            className="pointer-events-none"
                            type={Icons.CALENDAR}
                            size="Size_12"
                        />
                        {inputProps.endAdornment}
                    </>
                )}
            />
        );
    },
);

CalendarInput.displayName = 'CalendarInput';
